import { FC, useState } from 'react'
import Lottie from 'react-lottie'

import { Reservation } from 'src/types/reservation'
import { Address } from 'src/types/shipments'
import { GiftGiver } from 'src/routes/(registry)/registry/(gift-tracker-revamp)/context/GiftGiversProvider.types'
import ConfettiBurstDesktop from 'shared/lottie/ConfettiBurstDesktop.lottie.json'

import ContactInfo from 'src/routes/(registry)/registry/(gift-tracker-revamp)/components/GiftTrackerList/GiftTrackerListItem/GiftGiverInfo/ContactInfo'
import GiftDateAndCount from './GiftDateAndCount'
import GiftMessage from './GiftMessage'
import GiftProduct from './GiftProduct'
import GiftGiverInfo from './GiftGiverInfo'
import SurpriseProduct from './SurpriseProduct'
import css from './GiftTrackerListItem.styles.scss'

import useRevealSurprise from './useRevealSurprise'

const GiftProducts: FC<{ reservations: Reservation[] }> = ({
  reservations,
}) => (
  <div
    className={css.GiftTrackerListItem__GiftProducts}
    data-testid="GiftProducts"
  >
    {reservations.map((reservation) => (
      <GiftProduct key={reservation.id} reservation={reservation} />
    ))}
  </div>
)

const SurpriseItem: FC<{
  name: string
  email: string
  thankYouNoteAddress: Address | null
  mostRecentReservationAt: string
  revealSurprises: () => void
}> = ({
  name,
  email,
  mostRecentReservationAt,
  revealSurprises,
  thankYouNoteAddress,
}) => (
  <div
    className={css.GiftTrackerListItem}
    data-testid="GiftTracker__SurpriseItem"
  >
    <GiftGiverInfo
      email={email}
      name={name}
      thankYouNoteAddress={thankYouNoteAddress}
    />
    <GiftDateAndCount mostRecentReservationAt={mostRecentReservationAt} />
    <SurpriseProduct name={name} revealSurprises={revealSurprises} />
  </div>
)

const ConfettiAnimation: FC = () => {
  const [animationOver, setAnimationOver] = useState<boolean>(false)

  if (animationOver) {
    return null
  }

  return (
    <div
      className={css.GiftTrackerListItem__ConfettiFalling__Outer}
      data-testid="GiftTrackerListItem__ConfettiFalling"
    >
      <div className={css.GiftTrackerListItem__ConfettiFalling__Inner}>
        <Lottie
          eventListeners={[
            { eventName: 'complete', callback: () => setAnimationOver(true) },
          ]}
          options={{ animationData: ConfettiBurstDesktop, loop: false }}
          title="Confetti Burst"
        />
      </div>
    </div>
  )
}

const GiftTrackerListItem: FC<{ giftGiver: GiftGiver }> = ({ giftGiver }) => {
  const [isRevealedSurprise, revealSurprises] = useRevealSurprise(giftGiver.id)

  const {
    name,
    email,
    thankYouNoteAddress,
    mostRecentReservationAt,
    reservationCount,
    reservations,
    giftMessage,
    areAllSurprisesRevealedForUser,
    isAnyItemSurprise,
  } = giftGiver

  if (isAnyItemSurprise && !areAllSurprisesRevealedForUser) {
    return (
      <SurpriseItem
        email={email}
        mostRecentReservationAt={mostRecentReservationAt}
        name={name}
        revealSurprises={revealSurprises}
        thankYouNoteAddress={thankYouNoteAddress}
      />
    )
  }

  return (
    <div className={css.GiftTrackerListItem__Container}>
      <div className={css.GiftTrackerListItem__ContactCard}>
        <ContactInfo
          email={email}
          name={name}
          thankYouNoteAddress={thankYouNoteAddress}
        />
      </div>
      <div
        className={css.GiftTrackerListItem}
        data-testid="GiftTracker__GiftItem"
      >
        {isRevealedSurprise && <ConfettiAnimation />}
        <div className={css.GiftTrackerListItem__GiftGiverInfoHeader}>
          <GiftGiverInfo
            email={email}
            name={name}
            thankYouNoteAddress={thankYouNoteAddress}
          />
        </div>
        <GiftDateAndCount
          mostRecentReservationAt={mostRecentReservationAt}
          reservationCount={reservationCount}
        />
        <div className={css.GiftTrackerListItem__Content}>
          <GiftMessage giftMessage={giftMessage} />
          <GiftProducts reservations={reservations} />
        </div>
      </div>
    </div>
  )
}

export default GiftTrackerListItem
